import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ComparisonBar } from './ComparisonBar';

/**
 * Left column of the benchmark section: the three comparison bars (effort, cost,
 * duration) plus the modelled deviation from the model reference.
 */
export function ComparisonMetricsCard({ comparison, industryLabel, formatCost }) {
  const { t } = useTranslation();
  return (
    <Card className="bg-white border-[#DDE1E5]">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm text-[#66707A] uppercase tracking-wider flex items-center gap-2">
          {t('benchmark.yourEstimate')} vs {t('benchmark.industryAvg')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <ComparisonBar
          label={t('benchmark.effort')}
          testId="effort"
          yours={comparison.effort.yours}
          avg={comparison.effort.avg}
          diffPct={comparison.effort.diffPct}
          status={comparison.effort.status}
          formatValue={(v) => `${v} ${t('common.days')}`}
        />
        <ComparisonBar
          label={t('benchmark.totalCost')}
          testId="total-cost"
          yours={comparison.cost.yours}
          avg={comparison.cost.avg}
          diffPct={comparison.cost.diffPct}
          status={comparison.cost.status}
          formatValue={formatCost}
        />
        <ComparisonBar
          label={t('benchmark.durationLabel')}
          testId="duration"
          yours={comparison.duration.yours}
          avg={comparison.duration.avg}
          diffPct={comparison.duration.diffPct}
          status={comparison.duration.status}
          formatValue={(v) => `${v} ${t('common.weeks')}`}
        />

        {/* Model position — modelled deviation from the reference, not a percentile */}
        <div className="mt-4 p-4 rounded-lg bg-[#F5F6F7] border border-[#DDE1E5]">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#66707A] uppercase tracking-wider">{t('benchmark.modelPositionLabel')}</span>
            <span
              className="text-lg font-semibold text-[#171A1D]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="benchmark-model-position"
            >
              {comparison.effort.diffPct > 0 ? '+' : ''}{comparison.effort.diffPct}%
            </span>
          </div>
          <div className="mt-2 h-2 bg-[#DDE1E5] rounded-full overflow-hidden" aria-hidden="true">
            <div
              className="h-full rounded-full bg-[#171A1D] transition-all duration-500"
              style={{ width: `${Math.max(4, Math.min(100, 50 + comparison.effort.diffPct / 2))}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-[#8A929B]">
            {t('benchmark.modelPositionDesc', {
              status: t(comparison.effort.status.key, { defaultValue: comparison.effort.status.label }),
              industry: industryLabel,
            })}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
